import type {
  AplicarTiposRequest,
  AplicarTiposResponse,
  ClaveDeTipo,
  ListarMemoriaTiposResponse,
  MemoriaTipoDetalle,
  Receta,
  ReconocerTiposResponse,
  TiposDelPlanoResponse,
} from '@supernova/shared';
import { apiFetch } from './client.js';

/** «Conceptos por tipo» (memoria de tipos de ci): cliente de `/api/v1/…/tipos*` y `/api/v1/memoria-tipos*`. */
const base = (projectId: string, fileId: string) => `/api/v1/projects/${projectId}/planos/${fileId}/tipos`;

export function reconocerTipos(projectId: string, fileId: string, tipos: ClaveDeTipo[]): Promise<ReconocerTiposResponse> {
  return apiFetch<ReconocerTiposResponse>(`${base(projectId, fileId)}/reconocer`, { method: 'POST', body: { tipos } });
}

export function leerTiposDelPlano(projectId: string, fileId: string): Promise<TiposDelPlanoResponse> {
  return apiFetch<TiposDelPlanoResponse>(base(projectId, fileId));
}

export function marcarMemoriaLeida(projectId: string, fileId: string): Promise<{ leidaAt: string }> {
  return apiFetch<{ leidaAt: string }>(`${base(projectId, fileId)}/marcar-leida`, { method: 'POST', body: {} });
}

export function aplicarTiposDelPlano(projectId: string, fileId: string, body: AplicarTiposRequest): Promise<AplicarTiposResponse> {
  return apiFetch<AplicarTiposResponse>(base(projectId, fileId), { method: 'PUT', body });
}

/** Fuego y olvido: suma un uso a cada receta recordada que el armado aplicó. */
export function sumarUsosDeMemoria(ids: string[]): Promise<void> {
  if (ids.length === 0) return Promise.resolve();
  return apiFetch<void>('/api/v1/memoria-tipos/usos', { method: 'POST', body: { ids }, acceptStatus: [204] as number[] });
}

// --- Vista de un vistazo («Conceptos asignados») ---
export interface FiltrosDeMemoria {
  formato?: 'ifc' | 'dwg';
  estado?: 'no_vigente' | 'vigente';
  q?: string;
  soloMias?: boolean;
  cursor?: string;
  limite?: number;
}

export function listarMemoria(f: FiltrosDeMemoria): Promise<ListarMemoriaTiposResponse> {
  const p = new URLSearchParams();
  if (f.formato) p.set('formato', f.formato);
  if (f.estado) p.set('estado', f.estado);
  if (f.q?.trim()) p.set('q', f.q.trim());
  if (f.soloMias) p.set('soloMias', 'true');
  if (f.cursor) p.set('cursor', f.cursor);
  p.set('limite', String(f.limite ?? 100));
  return apiFetch<ListarMemoriaTiposResponse>(`/api/v1/memoria-tipos?${p.toString()}`);
}

export function detalleMemoria(id: string): Promise<MemoriaTipoDetalle> {
  return apiFetch<MemoriaTipoDetalle>(`/api/v1/memoria-tipos/${id}`);
}

export function editarMemoria(id: string, body: { receta: Receta; soloSugerir: boolean; version: number }): Promise<MemoriaTipoDetalle> {
  return apiFetch<MemoriaTipoDetalle>(`/api/v1/memoria-tipos/${id}`, { method: 'PUT', body });
}

export function restaurarMemoria(id: string, version: number, versionActual: number): Promise<MemoriaTipoDetalle> {
  return apiFetch<MemoriaTipoDetalle>(`/api/v1/memoria-tipos/${id}/restaurar`, { method: 'POST', body: { version, versionActual } });
}

export function borrarMemoria(id: string, version: number): Promise<void> {
  return apiFetch<void>(`/api/v1/memoria-tipos/${id}?version=${version}`, { method: 'DELETE', acceptStatus: [204] as number[] });
}
